<script>
  import {onMount} from 'svelte';
  import {fade, fly} from 'svelte/transition';
  import Metadata from '$features/seo/Metadata.svelte';
  import JsonLd from '$features/seo/JsonLd.svelte';
  import {appName, domain} from '$settings/global';
  import {Button} from '$components/jera';
  import ImageComparison from '$components/ImageComparison.svelte';
  import TypewriterText from '$components/TypewriterText.svelte';

  const heroMetrics = [
    {value: '<20s', label: 'generation time'},
    {value: '30-40%', label: 'return reduction'},
    {value: '20-30%', label: 'conversion increase'},
    {value: '24/7', label: 'automation'}
  ];

  const heroHighlights = [
    'Official Meta Tech Provider',
    'Instant try-on in customer DMs',
    'Multilingual AI concierge'
  ];

  const lookbookShots = [
    {
      type: 'image',
      tag: 'Moment One · Capture',
      title: 'Customer sends selfie',
      caption: 'Authentic photos captured directly in DMs. Natural, instant, zero friction.',
      image: '/images/tryon-before.png',
      alt: 'Customer selfie before virtual try-on',
      position: 'center top'
    },
    {
      type: 'image',
      tag: 'Moment Two · Transform',
      title: 'AI renders the look',
      caption: 'Photo-real try-on in under 20 seconds. Respects lighting, fit, and your brand aesthetic.',
      image: '/images/tryon-after.png',
      alt: 'Customer after AI outfit rendering',
      position: 'center'
    },
    {
      type: 'slider',
      tag: 'Moment Three · Convert',
      title: 'Customer sees & buys',
      caption: 'Interactive reveal drives desire. Checkout links, inventory sync, instant purchase.',
      before: '/images/tryon-before.png',
      after: '/images/tryon-after.png',
      split: 60
    }
  ];

  const automationPlaybooks = [
    {
      icon: 'bot',
      title: 'AI Concierge',
      description: 'Automated DM responses in your brand voice. Understands context, answers questions, guides purchases.',
      highlight: 'Trained on 10,000+ fashion conversations. Save 15-20 hours/week on customer support.'
    },
    {
      icon: 'users',
      title: 'Team Collaboration',
      description: 'Multi-user workspaces with role-based permissions. Approval workflows, CSR handoff for complex queries, and activity logs.',
      highlight: 'Centralized brand control. Everyone aligned, zero confusion.'
    },
    {
      icon: 'shopping-bag',
      title: 'Integrated Commerce',
      description: 'Live Shopify inventory, checkout links, and cart sync. Customers buy without leaving Instagram.',
      highlight: 'Discovery to purchase in one conversation. Reduce manual order entry by 95%.'
    },
    {
      icon: 'bar-chart',
      title: 'Real-Time Analytics',
      description: 'Track try-ons, conversions, and revenue by product, campaign, and channel. Export to any BI tool.',
      highlight: 'Full Instagram → Purchase attribution. Optimize ad spend by 40-60%.'
    },
    {
      icon: 'shield-check',
      title: 'Enterprise Security',
      description: 'Official Meta Tech Provider. Stripe billing, GDPR/PIPEDA compliance, encrypted processing. Audit trails and verified platform access.',
      highlight: 'Meta-verified partner. SOC 2 Type II certified. Avoid $50K-500K in GDPR fines.'
    }
  ];

  const plans = [
    {
      name: 'Start Free',
      price: '$0/month',
      description:
        'Test J\'ko risk-free with generous weekly credits. Perfect for boutiques exploring virtual try-ons before committing. No credit card required.',
      points: [
        'Unlimited SKUs (no per-garment fees)',
        'Instagram DM automation included',
        'Shopify integration with attribution',
        'Test with real customers, zero risk'
      ],
      href: 'https://dash.selify.ai'
    },
    {
      name: 'Scale Your Brand',
      price: 'From $49/month',
      description:
        'For serious brands ready to reduce returns and automate Instagram sales. Includes generous weekly credits, priority support, and full attribution tracking.',
      points: [
        'Reduce return costs by 30-40%',
        'Track Instagram → Purchase attribution',
        'Priority processing & support',
        'Advanced analytics & API access'
      ],
      featured: true,
      href: '/pricing'
    }
  ];

  const faqs = [
    {
      question: 'How much will we actually save on returns?',
      answer:
        'Industry data shows virtual try-on reduces fit-related returns by 30-40%. For a $1M/year fashion brand with a 30% return rate, that\'s $50,000-$100,000/year in saved return processing costs. Most brands see 15-50× ROI on their J\'ko investment within 6 months.'
    },
    {
      question: 'How quickly can we go live?',
      answer:
        'Most brands are converting customers within 30 minutes. Connect your Instagram Business Account, configure your brand voice, and watch J\'ko transform engagement into revenue. No technical expertise required—just vision.'
    },
    {
      question: 'How does team management work?',
      answer:
        'J\'ko includes multi-user workspaces with role-based permissions (Admin, Editor, Viewer). Marketing teams approve AI-generated content before it goes live, CSRs can jump into complex DM conversations, and everyone sees the same brand guidelines. Save 15-20 hours/week on coordination.'
    },
    {
      question: 'How do you handle customer privacy?',
      answer:
        'With the same rigor luxury brands demand. As an official Meta Tech Provider, we meet the highest platform standards. Every image is encrypted in transit, processed in secure environments, and permanently deleted post-generation. GDPR and PIPEDA compliant by design.'
    },
    {
      question: 'Will J\'ko integrate with our systems?',
      answer:
        'Seamlessly. Native Shopify integration, Stripe billing, and enterprise webhooks connect J\'ko to your existing infrastructure. Our REST API mirrors activity to any CRM or analytics platform you trust.'
    },
    {
      question: 'Can we maintain our brand voice?',
      answer:
        'This is where J\'ko excels. Every conversation element—from initial greeting to checkout confirmation—adapts to your tone. Our AI learns your voice, then amplifies it at scale without dilution. White-label options available for Enterprise customers.'
    }
  ];

  let animatedSections = $state(new Set());

  const getDelay = (index, base = 0) => base + index * 80;

  onMount(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-animate');
            if (id && !animatedSections.has(id)) {
              animatedSections = new Set([...animatedSections, id]);
            }
          }
        }
      },
      {threshold: 0.2}
    );

    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  });
</script>

<Metadata
  title="Selify AI — Reduce Returns 30-40% with AI Virtual Try-On"
  description="Serious software for fashion brands doing $100K-50M/year. Reduce returns, increase conversion, track Instagram → Purchase attribution. AI virtual try-on + automation + Shopify integration."
  canonicalUrl={domain}
  ogType="website"
  ogImage={`${domain}/images/selify-og-banner.jpg`}
  ogImageAlt="Selify AI - Reduce Fashion Returns with Virtual Try-On Technology"
  robots="index, follow"
/>

<JsonLd
  type="SoftwareApplication"
  data={{
    name: `${appName} — Reduce Returns with AI Virtual Try-On`,
    description:
      'B2B software for fashion brands. Reduce returns 30-40%, increase conversion 20-30%. AI virtual try-on + Instagram automation + Shopify attribution. Built for brands doing $100K-50M/year.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: '49',
      priceCurrency: 'USD',
      offerCount: '2',
      offers: [
        {
          '@type': 'Offer',
          name: 'Free Tier',
          price: '0',
          priceCurrency: 'USD',
          description: 'Test risk-free with weekly credits'
        },
        {
          '@type': 'Offer',
          name: 'Scale Your Brand',
          price: '49',
          priceCurrency: 'USD',
          description: 'For serious brands ready to reduce returns'
        }
      ]
    }
  }}
/>

<!-- WebSite Schema with SearchAction for GEO -->
<JsonLd
  type="WebSite"
  data={{
    name: appName,
    url: domain,
    description: 'AI virtual try-on platform for fashion brands. Reduce returns, increase conversions.',
    publisher: {
      '@type': 'Organization',
      name: appName,
      url: domain
    }
  }}
/>

<!-- Organization Schema for E-E-A-T -->
<JsonLd
  type="Organization"
  data={{
    name: appName,
    url: domain,
    logo: `${domain}/favicon.svg`,
    description: 'AI virtual try-on platform for fashion brands',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@selify.ai',
      contactType: 'customer support'
    },
    sameAs: [
      'https://dash.selify.ai'
    ]
  }}
/>

<!-- FAQPage Schema - Critical for AEO/GEO -->
<JsonLd
  type="FAQPage"
  data={{
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }}
/>

<main class="landing">
  <section class="hero" data-animate="hero" class:visible={animatedSections.has('hero')}>
    <div class="hero-shell">
      <div class="hero-copy">
        <span class="eyebrow" in:fade={{duration: 300}}>Serious Software for Serious Brands</span>
        <h1 in:fly={{y: 24, duration: 400}}>
          Stop Losing Money to Returns. Start Converting <TypewriterText words={['Instagram', 'DMs', 'Try-Ons', 'Shopify']} speed={120} deleteSpeed={60} pauseDuration={2500} /> into Sales.
        </h1>
        <p in:fade={{duration: 400, delay: 80}}>
          Reduce returns by 30-40%. Increase conversion by 20-30%. Track every Instagram post → DM → Try-on → Purchase. Selify combines AI virtual try-on + automation + Shopify attribution in one platform. Built for fashion brands doing $100K-50M/year.
        </p>

        <div class="cta-row" in:fade={{duration: 400, delay: 120}}>
          <Button variant="primary" href="https://dash.selify.ai">Launch the dashboard</Button>
          <Button variant="secondary" href="#pricing">See pricing</Button>
        </div>

        <div class="hero-highlights" in:fade={{duration: 400, delay: 140}}>
          {#each heroHighlights as highlight}
            <span class="highlight-pill">{highlight}</span>
          {/each}
        </div>

        <div class="hero-metrics">
          {#each heroMetrics as metric, i}
            <div
              class="metric-card"
              data-animate={`metric-${i}`}
              class:visible={animatedSections.has(`metric-${i}`)}
              in:fade={{duration: 300, delay: getDelay(i, 160)}}
            >
              <span class="metric-value">{metric.value}</span>
              <span class="metric-label">{metric.label}</span>
            </div>
          {/each}
        </div>
      </div>

      <div class="hero-visual" data-animate="slider" class:visible={animatedSections.has('slider')}>
        <div class="comparison-shell" in:fade={{duration: 400, delay: 200}}>
          <ImageComparison
            beforeSrc="/images/tryon-before.png"
            beforeAlt="Customer selfie before using Selify virtual try-on"
            afterSrc="/images/tryon-after.png"
            afterAlt="Customer wearing outfit generated by Selify virtual try-on"
            initialSplit={55}
          />
        </div>
      </div>
    </div>
  </section>

  <section class="lookbook" data-animate="lookbook" class:visible={animatedSections.has('lookbook')}>
    <div class="section-header">
      <span class="section-eyebrow">How It Works</span>
      <h2>From selfie to sale in three moments</h2>
      <p>
        Watch customers discover, visualize, and buy—powered by AI that understands your brand and delivers results.
      </p>
    </div>

    <div class="lookbook-grid">
      {#each lookbookShots as shot, i}
        <article
          class={`lookbook-card ${shot.type}`}
          data-animate={`lookbook-${i}`}
          class:visible={animatedSections.has(`lookbook-${i}`)}
          in:fade={{duration: 400, delay: getDelay(i, 120)}}
        >
          {#if shot.type === 'slider'}
            <div class="lookbook-media slider">
              <ImageComparison
                beforeSrc={shot.before}
                afterSrc={shot.after}
                showLabels={false}
                initialSplit={shot.split}
                beforeAlt="Before virtual try-on"
                afterAlt="After virtual try-on"
              />
            </div>
          {:else}
            <div
              class="lookbook-media"
              style={`background-image: url('${shot.image}'); background-position: ${shot.position ?? 'center'};`}
              role="img"
              aria-label={shot.alt}
            ></div>
          {/if}

          <div class="lookbook-meta">
            <span class="lookbook-tag">{shot.tag}</span>
            <h3>{shot.title}</h3>
            <p>{shot.caption}</p>
          </div>
        </article>
      {/each}
    </div>
  </section>

  <section
    class="automations"
    data-animate="automations"
    class:visible={animatedSections.has('automations')}
  >
    <div class="section-header compact">
      <span class="section-eyebrow">Intelligent by Design</span>
      <h2>Your always-on advantage</h2>
      <p>
        Five pillars of automation that don't just save time—they save you tens of thousands in costs, amplify your brand, deepen customer relationships, and unlock revenue streams you didn't know existed. This is AI that earns its place.
      </p>
    </div>

    <div class="automation-grid">
      {#each automationPlaybooks as playbook, i}
        <article
          class="automation-card"
          data-animate={`automation-${i}`}
          class:visible={animatedSections.has(`automation-${i}`)}
          style="--delay: {i * 0.1}s"
          in:fade={{duration: 400, delay: getDelay(i, 120)}}
        >
          <div class="automation-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              {#if playbook.icon === 'bot'}
                <path d="M12 8V4H8" />
                <rect width="16" height="12" x="4" y="8" rx="2" />
                <path d="M2 14h2" />
                <path d="M20 14h2" />
                <path d="M15 13v2" />
                <path d="M9 13v2" />
              {:else if playbook.icon === 'users'}
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              {:else if playbook.icon === 'shopping-bag'}
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              {:else if playbook.icon === 'bar-chart'}
                <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                <path d="M18 17V9" />
                <path d="M13 17V5" />
                <path d="M8 17v-3" />
              {:else if playbook.icon === 'shield-check'}
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                <path d="m9 12 2 2 4-4" />
              {/if}
            </svg>
          </div>
          <div class="automation-body">
            <h3>{playbook.title}</h3>
            <p>{playbook.description}</p>
            <span class="automation-highlight">{playbook.highlight}</span>
          </div>
        </article>
      {/each}
    </div>
  </section>

  <section
    class="pricing"
    id="pricing"
    data-animate="pricing"
    class:visible={animatedSections.has('pricing')}
  >
    <div class="section-header">
      <span class="section-eyebrow">Investment in Excellence</span>
      <h2>Pricing that scales with your vision</h2>
      <p>
        Start lean, scale confidently. Whether you're testing a new channel or transforming your entire social commerce strategy, Selify grows with you—never holding you back, always moving you forward.
      </p>
    </div>

    <div class="pricing-grid">
      {#each plans as plan, i}
        <article
          class="pricing-card"
          data-animate={`plan-${i}`}
          class:featured={plan.featured}
          class:visible={animatedSections.has(`plan-${i}`)}
          in:fade={{duration: 400, delay: getDelay(i, 160)}}
        >
          <div class="pricing-heading">
            <span class="plan-name">{plan.name}</span>
            <span class="plan-price">{plan.price}</span>
          </div>
          <p class="plan-description">{plan.description}</p>
          <ul>
            {#each plan.points as point}
              <li>{point}</li>
            {/each}
          </ul>
          <Button fullWidth variant={plan.featured ? 'primary' : 'secondary'} href={plan.href}>
            Start with {plan.name.toLowerCase()}
          </Button>
        </article>
      {/each}
    </div>
  </section>

  <section class="faq" data-animate="faq" class:visible={animatedSections.has('faq')}>
    <div class="section-header compact">
      <span class="section-eyebrow">Before You Begin</span>
      <h2>The questions brands ask before they transform</h2>
    </div>

    <div class="faq-grid">
      {#each faqs as item, i}
        <article
          class="faq-item"
          data-animate={`faq-${i}`}
          class:visible={animatedSections.has(`faq-${i}`)}
          in:fade={{duration: 400, delay: getDelay(i, 120)}}
        >
          <h3>{item.question}</h3>
          <p>{item.answer}</p>
        </article>
      {/each}
    </div>
  </section>

  <section class="cta" data-animate="cta" class:visible={animatedSections.has('cta')}>
    <div class="cta-shell">
      <div class="cta-content" in:fade={{duration: 400}}>
        <span class="section-eyebrow">Join the Future</span>
        <h2>Your customers are ready. Your competition isn't.</h2>
        <p>
          While others debate, leading brands are already converting. The technology is proven, the infrastructure is live, and your first customers are waiting. The only question: will you lead, or watch others transform the category?
        </p>
        <div class="cta-actions">
          <Button variant="primary" href="https://dash.selify.ai">Start converting today</Button>
          <Button variant="secondary" href="mailto:hello@selify.ai">Request private demo</Button>
        </div>
      </div>
    </div>
  </section>
</main>

<style lang="postcss">
  @reference '$theme';

  .landing {
    @apply min-h-screen bg-base0 text-base5;
  }

  section {
    @apply relative;
  }

  .hero {
    @apply overflow-hidden pt-32 pb-24 bg-base0;
    position: relative;
  }

  .hero::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--color-border) 50%, transparent);
  }

  .hero.visible {
    @apply opacity-100;
  }

  .hero-shell {
    @apply mx-auto flex w-full max-w-7xl flex-col gap-16 px-6 lg:flex-row lg:items-center lg:gap-20;
  }

  .hero-copy {
    @apply flex w-full flex-col gap-6 lg:max-w-xl;
    /* Prevent text reflow by setting explicit flex basis */
    flex: 0 0 auto;
  }

  .eyebrow {
    @apply text-sm font-medium uppercase tracking-widest text-base4;
  }

  h1 {
    @apply text-4xl font-semibold leading-tight text-base6 md:text-5xl lg:text-6xl;
  }

  .hero-copy p {
    @apply text-base leading-relaxed text-base4 md:text-lg;
  }

  .cta-row {
    @apply flex flex-wrap items-center gap-4 pt-4;
  }

  .hero-highlights {
    @apply flex flex-wrap gap-2 pt-4;
  }

  .highlight-pill {
    @apply inline-flex items-center rounded-full px-4 py-1.5 text-xs font-medium tracking-wide;
    background: rgba(var(--color-base1-rgb), 0.6);
    color: var(--color-base5);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(var(--color-base3-rgb), 0.2);
  }

  :root[data-theme='miozu-dark'] .highlight-pill {
    background: rgba(var(--color-base2-rgb), 0.5);
    color: var(--color-base5);
    border: 1px solid rgba(var(--color-base3-rgb), 0.25);
  }

  .hero-metrics {
    @apply grid grid-cols-2 gap-4 pt-8 sm:grid-cols-4;
  }

  .metric-card {
    @apply rounded-xl px-4 py-5 text-center opacity-0 translate-y-4;
    background: rgba(var(--color-base1-rgb), 0.5);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(var(--color-base3-rgb), 0.2);
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  :root[data-theme='miozu-dark'] .metric-card {
    background: rgba(var(--color-base2-rgb), 0.4);
    border: 1px solid rgba(var(--color-base3-rgb), 0.3);
  }

  .metric-card.visible {
    @apply opacity-100 translate-y-0;
  }

  .metric-value {
    @apply block text-2xl font-bold mb-1;
    color: var(--color-base6);
  }

  .metric-label {
    @apply block text-[11px] font-medium uppercase tracking-wider;
    color: var(--color-base4);
  }

  .hero-visual {
    @apply relative flex w-full items-center justify-center;
    min-height: 600px; /* Reserve space to prevent layout shift */
  }

  .comparison-shell {
    @apply relative w-full max-w-[520px];
    /* Define explicit aspect ratio to reserve space before images load */
    aspect-ratio: 520 / 650;
  }

  .comparison-shell::before {
    content: '';
    @apply absolute -inset-8 rounded-[40px];
    background: radial-gradient(80% 80% at 50% 0%, rgba(233, 140, 70, 0.3), transparent);
    z-index: 0;
  }

  .comparison-shell :global(.comparison-container) {
    @apply relative z-10 mx-auto h-auto overflow-hidden rounded-[32px];
    width: calc(100% - 3rem);
    max-width: 480px;
  }

  /* Shadow layer using pseudo-element */
  .comparison-shell :global(.comparison-container::before) {
    @apply absolute pointer-events-none rounded-[32px];
    content: '';
    inset: -60px;
    box-shadow: 0 40px 120px rgba(13, 21, 32, 0.45);
    z-index: -1;
  }

  /* Force border-radius on all children */
  .comparison-shell :global(.comparison-container *) {
    @apply rounded-[inherit];
  }

  @media (max-width: 768px) {
    .comparison-shell :global(.comparison-container) {
      width: calc(100% - 2rem);
    }
  }

  .lookbook,
  .automations,
  .pricing,
  .faq {
    @apply mx-auto max-w-6xl px-6 py-20;
  }

  .section-header {
    @apply mx-auto flex max-w-3xl flex-col gap-4 text-center mb-12;
  }

  .section-header.compact {
    @apply mb-10;
  }

  .section-eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.35em] text-base4;
  }

  .section-header h2 {
    @apply text-3xl font-semibold text-base5 md:text-4xl;
  }

  .section-header p {
    @apply text-base text-base4 md:text-lg;
  }

  .lookbook-grid {
    @apply grid gap-6 md:grid-cols-3;
  }

  .lookbook-card {
    @apply flex h-full flex-col gap-5 rounded-[28px] border border-border/60 bg-base1 p-6 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-lg opacity-0 translate-y-2;
  }

  .lookbook-card.visible {
    @apply opacity-100 translate-y-0;
  }

  .lookbook-media {
    @apply relative aspect-[4/5] w-full overflow-hidden;
    border-radius: 32px;
    background-size: cover;
    background-repeat: no-repeat;
  }

  .lookbook-media.slider {
    @apply aspect-[4/5];
  }

  .lookbook-media.slider :global(.comparison-wrapper) {
    @apply h-full min-h-0;
  }

  .lookbook-tag {
    @apply text-[11px] font-semibold uppercase tracking-[0.4em] text-base3;
  }

  .lookbook-meta h3 {
    @apply mt-1 text-xl font-semibold text-base6;
  }

  .lookbook-meta p {
    @apply mt-2 text-sm text-base4;
  }

  .automations {
    @apply mx-auto max-w-7xl px-6 py-24 bg-base1 rounded-[3rem];
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .automations.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .automation-grid {
    @apply grid gap-6 md:grid-cols-2;
  }

  .automation-card {
    @apply flex gap-6 rounded-2xl border border-border/60 bg-base0 p-6 backdrop-blur;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .automation-card.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .automation-card:hover {
    @apply -translate-y-1 border-base14/40 shadow-md;
  }

  .automation-icon {
    @apply flex h-14 w-14 shrink-0 items-center justify-center rounded-xl;
    background: linear-gradient(135deg, var(--color-base14) 0%, var(--color-base13) 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(var(--color-base14-rgb, 233, 140, 70), 0.25);
  }

  .automation-body {
    @apply flex flex-1 flex-col gap-2;
  }

  .automation-body h3 {
    @apply text-xl font-semibold text-base6;
  }

  .automation-body p {
    @apply text-sm text-base4 leading-relaxed;
  }

  .automation-highlight {
    @apply mt-3 inline-flex text-xs font-semibold uppercase tracking-wider text-base14;
  }

  .pricing-grid {
    @apply grid gap-6 md:grid-cols-2;
  }

  .pricing-card {
    @apply flex h-full flex-col gap-6 rounded-3xl p-8;
    /* Apple-style glassmorphism */
    background: rgba(var(--color-base1-rgb), 0.5);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(var(--color-base3-rgb), 0.18);
    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .pricing-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 64px 0 rgba(0, 0, 0, 0.15);
  }

  :root[data-theme='miozu-dark'] .pricing-card {
    background: rgba(var(--color-base2-rgb), 0.35);
    border: 1px solid rgba(var(--color-base3-rgb), 0.25);
    -webkit-backdrop-filter: blur(20px) saturate(150%);
    backdrop-filter: blur(20px) saturate(150%);
  }

  .pricing-card.featured {
    @apply border-base14/60 bg-base14/10 shadow-lg;
  }

  .pricing-heading {
    @apply flex flex-col gap-2;
  }

  .plan-name {
    @apply text-sm font-semibold uppercase tracking-[0.25em] text-base3;
  }

  .plan-price {
    @apply text-3xl font-semibold text-base6;
  }

  .plan-description {
    @apply text-base text-base4;
  }

  .pricing-card ul {
    @apply flex flex-col gap-2 text-sm text-base4;
  }

  .pricing-card li::before {
    content: '–';
    @apply mr-2 text-base14;
  }

  .faq-grid {
    @apply grid gap-6 md:grid-cols-2;
  }

  .faq-item {
    @apply rounded-3xl border border-border/60 bg-base1 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-md opacity-0 translate-y-3;
  }

  .faq-item.visible {
    @apply opacity-100 translate-y-0;
  }

  .faq-item h3 {
    @apply text-lg font-semibold text-base6;
  }

  .faq-item p {
    @apply mt-3 text-base text-base4;
  }

  .cta {
    @apply px-6 pb-24 pt-12;
  }

  .cta-shell {
    @apply mx-auto max-w-5xl rounded-[36px] border border-border bg-gradient-to-br from-base2 via-base1 to-base14/10 px-8 py-12 text-base5 shadow-lg;
  }

  .cta-content {
    @apply flex flex-col gap-6 text-center;
  }

  .cta-content h2 {
    @apply text-3xl font-semibold text-base6 md:text-4xl;
  }

  .cta-content p {
    @apply text-base text-base4 md:text-lg;
  }

  .cta-actions {
    @apply flex flex-wrap justify-center gap-4 pt-2;
  }

  @media (max-width: 1024px) {
    .hero {
      @apply pt-20;
    }

    .hero-shell {
      @apply gap-12;
    }

    .hero-visual {
      min-height: 500px;
    }
  }

  @media (max-width: 768px) {
    .hero-shell {
      @apply px-4;
    }

    .hero-visual {
      min-height: 450px;
    }

    .lookbook,
    .automations,
    .pricing,
    .faq {
      @apply px-4 py-16;
    }

    .hero-metrics {
      @apply grid-cols-2 gap-3;
    }

    .lookbook-card,
    .automation-card,
    .pricing-card,
    .faq-item {
      @apply p-6;
    }

    .cta-shell {
      @apply px-6 py-10;
    }
  }
</style>
